<template>
  <section class="page-wrap">
    <UiPageHero
      eyebrow="Báo cáo"
      title="Xuất báo cáo tài sản"
      description="Tạo báo cáo theo giá trị hoặc báo cáo tổng hợp theo năm và khoa/phòng."
    />

    <div class="grid gap-4 xl:grid-cols-2">
      <form class="panel-pad" @submit.prevent="createValueReport">
        <div class="flex flex-col gap-1 text-left">
          <p class="text-lg font-bold text-ink">Xuất báo cáo theo giá trị</p>
        </div>

        <div class="mt-4 grid gap-4 md:grid-cols-3">
          <UiSelect v-model.number="valueKind" label="Chọn loại thống kê">
            <option :value="1">Báo cáo lớn hơn</option>
            <option :value="2">Báo cáo nhỏ hơn</option>
          </UiSelect>

          <UiSelect v-model.number="valueLoai" label="Chọn loại">
            <option
              v-for="item in dsLoai"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </UiSelect>

          <UiInput
            :model-value="valuePriceInput"
            label="Nhập giá trị:"
            inputmode="numeric"
            @keydown="blockNonNumericKey"
            @paste="pasteNumericOnly($event, updateValuePrice)"
            @update:modelValue="updateValuePrice"
          />
        </div>

        <UiButton
          type="submit"
          class="mt-4"
          icon="export"
          :loading="loadingValue"
          loading-text="Đang tạo..."
        >
          Tạo báo cáo
        </UiButton>
      </form>

      <form class="panel-pad" @submit.prevent="createSummaryReport">
        <div class="flex flex-col gap-1 text-left">
          <p class="text-lg font-bold text-ink">Xuất báo cáo tổng hợp</p>
        </div>

        <div class="mt-4 grid gap-4 md:grid-cols-3">
          <UiInput
            v-model.number="year"
            label="Nhập năm thống kê:"
            type="number"
            min="0"
          />

          <UiSelect
            v-model.number="departmentId"
            label="Chọn khoa phòng thống kê"
          >
            <option :value="0">Toàn viện</option>
            <option
              v-for="item in departments"
              :key="item.id"
              :value="item.id"
            >
              {{ item.tenKhoaPhong }}
            </option>
          </UiSelect>

          <UiSelect v-model.number="summaryLoai" label="Chọn loại">
            <option
              v-for="item in dsLoai"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </UiSelect>
        </div>

        <UiButton
          type="submit"
          class="mt-4"
          icon="export"
          :loading="loadingSummary"
          loading-text="Đang tạo..."
        >
          Tạo báo cáo
        </UiButton>
      </form>
    </div>

  </section>
</template>

<script lang="ts">
import { onMounted, ref } from "vue";
import QLTSRepository from "@/services/QLTSRepository";
import { KhoaPhong } from "@/models/khoaPhong";
import { getErrorMessage, useToast } from "@/utils/toast";
import {
  blockNonNumericKey,
  formatIntegerInput,
  parseIntegerInput,
  pasteNumericOnly,
} from "@/utils/format";

export default {
  setup() {
    const departments = ref<KhoaPhong[]>([]);
    const dsLoai = [
      { label: "Hành chính", value: 1 },
      { label: "Chuyên môn", value: 2 },
    ];
    const valuePrice = ref(0);
    const valuePriceInput = ref("");
    const valueKind = ref(1);
    const valueLoai = ref(1);
    const year = ref(0);
    const departmentId = ref(0);
    const summaryLoai = ref(1);
    const loadingValue = ref(false);
    const loadingSummary = ref(false);
    const { showToast } = useToast();

    onMounted(() => {
      QLTSRepository.getListKhoaPhong()
        .then((res) => (departments.value = res.data || []))
        .catch((err) => {
          showToast(
            getErrorMessage(err, "Không tải được danh sách khoa/phòng."),
            "error",
          );
        });
    });

    const downloadReportFile = (blob: Blob, fileName: string) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const extension = fileName.includes(".")
        ? fileName.slice(fileName.lastIndexOf("."))
        : ".xlsx";
      link.href = url;
      link.download = `DanhSachThongKe${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    };

    const handleResult = (data: any) => {
      const fileName = String(data || "");
      if (!fileName) throw new Error("API không trả về tên file báo cáo.");
      return QLTSRepository.downloadListChart(fileName).then((res) => {
        downloadReportFile(res.data, fileName);
      });
    };

    const handleDownloadSuccess = () => {
      showToast("Đã tạo báo cáo.", "success");
    };

    const handleError = (err: any) => {
      showToast(getErrorMessage(err, "Không tạo được báo cáo."), "error");
    };

    const updateValuePrice = (value: string | number) => {
      valuePrice.value = parseIntegerInput(value);
      valuePriceInput.value = formatIntegerInput(value);
    };

    const createValueReport = () => {
      loadingValue.value = true;
      QLTSRepository.createBaoCaoTheoGiaTri(
        Number(valuePrice.value || 0),
        valueKind.value,
        valueLoai.value,
      )
        .then((res) => handleResult(res.data))
        .then(handleDownloadSuccess)
        .catch(handleError)
        .finally(() => (loadingValue.value = false));
    };

    const createSummaryReport = () => {
      loadingSummary.value = true;
      QLTSRepository.createBaoCaoTongHop(
        Number(year.value || 0),
        departmentId.value,
        summaryLoai.value,
      )
        .then((res) => handleResult(res.data))
        .then(handleDownloadSuccess)
        .catch(handleError)
        .finally(() => (loadingSummary.value = false));
    };

    return {
      departments,
      dsLoai,
      valuePrice,
      valuePriceInput,
      updateValuePrice,
      blockNonNumericKey,
      pasteNumericOnly,
      valueKind,
      valueLoai,
      year,
      departmentId,
      summaryLoai,
      loadingValue,
      loadingSummary,
      createValueReport,
      createSummaryReport,
    };
  },
};
</script>
